// @ts-check
// Tutorial catalog. Each entry has a printable title, a body (markdown-ish
// chunks rendered into `<p>` blocks), and a sequence of step objects that the
// in-app overlay (driver.js) can replay. Same content powers the HQ + desktop
// Help pages so the documentation stays consistent.

/** @typedef {{ title: string, body: string[], steps: { selector?: string, title: string, text: string }[] }} Tutorial */

/** @type {Record<string, Tutorial>} */
export const TUTORIALS = {
  'getting-started': {
    title: 'Getting started',
    body: [
      'Welcome to Katsura POS. The system has two surfaces: the Desktop app each branch runs, and the HQ Admin web app where SUPER_ADMIN authors masters that sync down.',
      'Default credentials seeded for dev: admin@katsura.local / change-me-now. Rotate before any real use.',
      'Sync runs every 5 minutes automatically and on-demand from the dashboard.',
    ],
    steps: [
      { selector: '[data-tour="sidebar"]', title: 'Navigation', text: 'Use the sidebar to jump between modules. POS for billing, Products for catalog, Recon for stocktake.' },
      { selector: '[data-tour="profile"]', title: 'Profile', text: 'Your role + branch is shown here. Sign out from the bottom button.' },
    ],
  },

  'product-mrp': {
    title: 'Add an MRP product',
    body: [
      'MRP products are priced tax-inclusive: the customer pays the printed MRP and tax is back-computed from gst_rate.',
      'When creating an MRP product the MRP field is required. The Sale price is the discounted price you actually charge — it can be the same as MRP if no discount applies.',
      'Barcode is auto-generated (Code128, prefix K) if you leave it blank, so labels print correctly even on day one.',
    ],
    steps: [
      { selector: '[data-tour="new-product"]', title: 'Open the form', text: 'Click "+ New product" to open the create dialog.' },
      { selector: '[data-tour="pricing-mode"]', title: 'Pick MRP mode', text: 'Pricing mode = MRP. The MRP field will appear; LOOSE hides it.' },
      { selector: '[data-tour="mrp-input"]', title: 'Enter MRP', text: 'Type the printed MRP in rupees. Tax-inclusive.' },
      { selector: '[data-tour="sale-input"]', title: 'Enter sale price', text: 'The default selling price. Cashier can override at POS within brand discount caps.' },
    ],
  },

  'product-loose': {
    title: 'Add a LOOSE / weighed product',
    body: [
      'LOOSE products do not carry a printed MRP. The cashier enters the rate per stock UOM at billing.',
      'For DUOM (purchased by weight, sold by piece) set purchase_uom = KG and stock_uom = PCS, then enter the avg weight per piece — the system converts kg purchases into pieces automatically.',
    ],
    steps: [
      { selector: '[data-tour="pricing-mode"]', title: 'Pick LOOSE mode', text: 'Pricing mode = LOOSE. MRP field hides automatically.' },
      { selector: '[data-tour="purchase-uom"]', title: 'Purchase UOM', text: 'Choose KG if you buy by weight. DUOM warning appears when stock UOM differs.' },
      { selector: '[data-tour="avg-weight"]', title: 'Average weight', text: 'Enter the average kg per piece. Used during GRN to convert kg purchased into stock pieces.' },
    ],
  },

  'purchase-grn': {
    title: 'Create a purchase / GRN',
    body: [
      'A purchase / Goods Receipt Note creates one or more batches at the cost of that purchase — each batch keeps its own cost so margins stay accurate even when supplier rates change.',
      'Batch numbers and batch barcodes are auto-generated if not supplied: batch_no = GRN-SKU, batch barcode = Code128 of the batch ULID.',
      'Bulk upload from HQ Purchases tab accepts CSV with columns sku,qty,rate_rupees — one GRN per upload.',
    ],
    steps: [
      { selector: '[data-tour="grn-header"]', title: 'Pick vendor + mode', text: 'Vendor, mode (MRP/LOOSE/DUOM), and state for GST computation.' },
      { selector: '[data-tour="grn-lines"]', title: 'Add lines', text: 'Each line = product + qty + rate. Lines create batches + update stock + post a stock_movement.' },
      { selector: '[data-tour="grn-csv"]', title: 'CSV bulk', text: 'Paste rows like "HQ-001,10,135" — backend ingests all lines in one transaction.' },
    ],
  },

  'bill-pos': {
    title: 'Bill at POS',
    body: [
      'Add lines by scanning the barcode (auto-focus on the input) or searching by name/SKU. Per-line discount is checked against brand discount caps before save.',
      'Total is computed live with CGST/SGST/IGST split based on customer place_of_supply.',
      'Print fires the Retsol RTP-82UE thermal printer; if the device is unplugged the receipt is spooled to ~/.config/@katsura/desktop/print-spool/ for later replay.',
    ],
    steps: [
      { selector: '[data-tour="scan-input"]', title: 'Scan or type', text: 'Focus this input and scan the product barcode. Recognized barcodes auto-add a line with qty 1.' },
      { selector: '[data-tour="cart"]', title: 'Cart', text: 'Edit qty / unit price / discount inline. Brand caps reject overdiscount before save.' },
      { selector: '[data-tour="checkout"]', title: 'Complete', text: 'Choose payment mode(s). On complete the bill prints and the cash drawer kicks.' },
    ],
  },

  'recon': {
    title: 'Stock reconciliation',
    body: [
      'Open a count session, scan each product on the shelf, enter counted qty. System computes variance against current stock.',
      'When complete, post the session: variance != 0 lines emit stock_movements (ADJUST) and update the stock aggregate. The session is sealed and visible in audit history.',
      'Always recount variance-heavy lines before posting — posted sessions can only be reversed by a new opposing recon.',
    ],
    steps: [
      { selector: '[data-tour="recon-new"]', title: 'New session', text: 'Click "New count" to begin. The system snapshots stock per line as you add it.' },
      { selector: '[data-tour="recon-line-input"]', title: 'Add line', text: 'Scan or pick a product, type the counted qty. Variance auto-computes.' },
      { selector: '[data-tour="recon-post"]', title: 'Post', text: 'Move to Review → Post. Posting writes stock_movements + locks the session.' },
    ],
  },

  'barcode-print': {
    title: 'Print product barcodes',
    body: [
      'Single labels print one at a time to the thermal printer (one strip per qty).',
      'Bulk mode renders an A4 PDF (3 cols × 8 rows = 24 labels per page) saved to ~/.config/@katsura/desktop/barcode-sheets/. Open in any PDF viewer and send to your label printer.',
    ],
    steps: [
      { selector: '[data-tour="bp-pick"]', title: 'Pick products', text: 'Search and tick the products + set qty per product.' },
      { selector: '[data-tour="bp-mode"]', title: 'Choose mode', text: 'Thermal (one strip at a time) or A4 sheet (PDF download).' },
      { selector: '[data-tour="bp-print"]', title: 'Print', text: 'On thermal mode the printer fires immediately. On A4 mode you get a PDF path.' },
    ],
  },
};

export function tutorialList() {
  return Object.entries(TUTORIALS).map(([key, t]) => ({ key, title: t.title }));
}
